// DATA ACCESS LAYER
// All new features MUST use this adapter instead of importing
// firebase/firestore directly. This enables future migration to
// Supabase or any other data store without touching component code.
// See ARCHITECTURE_AUDIT.md (RW-352) for context.
//
// Path format: collection paths use slash-delimited strings.
//   'users'                            → collection
//   'transactions/abc/checklistItems'  → subcollection
//
// Query builder: chain .where() / .orderBy() / .limit() then await
// .get() or pass to .subscribe(cb). Operators mirror Firestore's:
// '==', '!=', '<', '<=', '>', '>=', 'in', 'not-in',
// 'array-contains', 'array-contains-any'.

import {
  collection,
  collectionGroup as fsCollectionGroup,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query as fsQuery,
  where as fsWhere,
  orderBy as fsOrderBy,
  limit as fsLimit,
  startAfter as fsStartAfter,
  runTransaction as fsRunTransaction,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// ─── multi-tenant injection (RW-544 Tier 2) ───────────────────────
// TenantContext registers the resolved tenantId via _setCurrentTenantId()
// when its claim subscription resolves. data.js then auto-stamps every
// create / setDocument / batch.set write with `tenantId` so callers don't
// have to thread it through. Updates are NOT auto-stamped — tenantId on a
// document is treated as immutable after creation. Cross-tenant infra
// collections (rateLimits, platformErrors, etc.) opt out via the
// CROSS_TENANT_PATH_PREFIXES set below.
//
// Tier 2 stays permissive: if no tenantId is registered (pre-auth window
// or signed-out call), writes go through without stamping. Tier 3 will
// tighten via Firestore rules.

let _currentTenantId = null;

// RW-544 Tier 4 (Phase 1): adoptionMetrics, errorLogs, migrations, tenants
// added. adoptionMetrics is a platform-wide aggregate (one doc per day,
// not per tenant — RW-548 will model per-tenant); errorLogs / migrations /
// tenants are platform-level namespaces that intentionally span tenants.
// The pre-flight script's PREFLIGHT_ADDITIONS in-memory exclusion for
// adoptionMetrics is now redundant with this entry; analytics stays in
// PREFLIGHT_ADDITIONS only until RW-557 lands its architectural fix.
const CROSS_TENANT_PATH_PREFIXES = new Set([
  '_server',
  'adoptionMetrics',
  'errorLinearMap',
  'errorLogs',
  'functionHealth',
  'linkPreviews',
  'migrations',
  'neighborhoodCoordinates',
  'platformErrors',
  'rateLimits',
  'system',
  'tenants',
]);

function _isCrossTenantPath(path) {
  const head = String(path || '').split('/').filter(Boolean)[0];
  return CROSS_TENANT_PATH_PREFIXES.has(head);
}

function _stampTenant(path, data) {
  if (!data || typeof data !== 'object') return data;
  if (data.tenantId) return data;
  if (!_currentTenantId) return data;
  if (_isCrossTenantPath(path)) return data;
  return { ...data, tenantId: _currentTenantId };
}

export function _setCurrentTenantId(id) {
  _currentTenantId = id ? String(id) : null;
}

export function _getCurrentTenantId() {
  return _currentTenantId;
}

/**
 * withTenant(path, data) — public Tier 2 helper for call sites that bypass
 * the data.js adapter (legacy Firestore-direct components). Returns the
 * payload with `tenantId` stamped if (a) the registered tenant is set, (b)
 * the payload doesn't already carry a tenantId, and (c) the path isn't a
 * cross-tenant-infrastructure collection. Permissive on missing tenant —
 * matches the rest of data.js behavior during the rollout.
 *
 * Usage at a legacy call site:
 *   import { withTenant } from '../services/data';
 *   await addDoc(collection(db, 'transactions'), withTenant('transactions', payload));
 *   await setDoc(docRef, withTenant('transactions/x/timeline', entry));
 *   batch.set(ref, withTenant('contacts', payload));
 */
export function withTenant(path, data) {
  return _stampTenant(path, data);
}

const toSnap = (snap) =>
  snap.exists() ? { id: snap.id, ...snap.data() } : null;

const toList = (snap) =>
  snap.docs.map((d) => ({ id: d.id, ...d.data() }));

const pathParts = (path) => path.split('/').filter(Boolean);

const refFor = (path) => {
  const parts = pathParts(path);
  if (parts.length === 0) throw new Error('data.js: empty path');
  return parts.length % 2 === 1
    ? collection(db, ...parts)
    : doc(db, ...parts);
};

const collRef = (path) => {
  const parts = pathParts(path);
  if (parts.length % 2 !== 1) {
    throw new Error(`data.js: expected collection path, got "${path}"`);
  }
  return collection(db, ...parts);
};

const docRef = (path, id) => {
  const parts = pathParts(path);
  if (id !== undefined) return doc(db, ...parts, id);
  if (parts.length % 2 !== 0) {
    throw new Error(`data.js: expected document path, got "${path}"`);
  }
  return doc(db, ...parts);
};

// ─── simple CRUD ──────────────────────────────────────────

export async function get(path, id) {
  const snap = await getDoc(docRef(path, id));
  return toSnap(snap);
}

export async function list(path, filters = {}) {
  const built = buildQuery(collRef(path), filters);
  const snap = await getDocs(built);
  return toList(snap);
}

export async function create(path, data) {
  const ref = await addDoc(collRef(path), _stampTenant(path, data));
  return { id: ref.id };
}

export async function setDocument(path, id, data, options = {}) {
  // For non-merge writes the doc is being created/replaced wholesale —
  // stamp tenantId. For merge writes we still stamp (a missing tenantId on
  // the existing doc gets backfilled, an existing tenantId is preserved).
  await setDoc(docRef(path, id), _stampTenant(path, data), options);
}

export async function update(path, id, data) {
  // Updates do NOT auto-stamp tenantId — it's immutable after creation.
  await updateDoc(docRef(path, id), data);
}

export async function remove(path, id) {
  await deleteDoc(docRef(path, id));
}

// ─── query builder ────────────────────────────────────────

function buildQuery(ref, filters) {
  const constraints = [];
  if (Array.isArray(filters.where)) {
    for (const [field, op, value] of filters.where) {
      constraints.push(fsWhere(field, op, value));
    }
  }
  if (Array.isArray(filters.orderBy)) {
    for (const ob of filters.orderBy) {
      if (Array.isArray(ob)) constraints.push(fsOrderBy(ob[0], ob[1]));
      else constraints.push(fsOrderBy(ob));
    }
  } else if (filters.orderBy) {
    constraints.push(fsOrderBy(filters.orderBy));
  }
  // Cursor values pair positionally with the orderBy fields above (RW-835).
  if (Array.isArray(filters.startAfter) && filters.startAfter.length) {
    constraints.push(fsStartAfter(...filters.startAfter));
  }
  if (filters.limit) constraints.push(fsLimit(filters.limit));
  return constraints.length ? fsQuery(ref, ...constraints) : ref;
}

export function query(path) {
  return queryOver(() => collRef(path), toList);
}

// RW-1635: collection-group variant — the same chainable builder, spanning
// every subcollection with this id. Because a group result is ambiguous
// without its location, each row additionally carries `_path` (the document
// path in the adapter's slash-delimited addressing scheme) so callers can
// derive the parent document id without touching raw refs.
export function groupQuery(collectionId) {
  return queryOver(
    () => fsCollectionGroup(db, collectionId),
    (snap) => snap.docs.map((d) => ({ id: d.id, _path: d.ref.path, ...d.data() })),
  );
}

function queryOver(refFactory, mapSnap) {
  const wheres = [];
  const orders = [];
  let cursor;
  let lim;
  const filters = () => ({
    where: wheres,
    orderBy: orders,
    startAfter: cursor,
    limit: lim,
  });
  const api = {
    where(field, op, value) {
      wheres.push([field, op, value]);
      return api;
    },
    orderBy(field, direction) {
      orders.push(direction ? [field, direction] : [field]);
      return api;
    },
    startAfter(...values) {
      cursor = values;
      return api;
    },
    limit(n) {
      lim = n;
      return api;
    },
    async get() {
      const snap = await getDocs(buildQuery(refFactory(), filters()));
      return mapSnap(snap);
    },
    async count() {
      const snap = await getCountFromServer(buildQuery(refFactory(), filters()));
      return snap.data().count;
    },
    subscribe(callback, onError) {
      const built = buildQuery(refFactory(), filters());
      return onSnapshot(built, (snap) => callback(mapSnap(snap)), onError);
    },
  };
  return api;
}

// ─── real-time ────────────────────────────────────────────

export function subscribe(path, filters, callback, onError) {
  // subscribe('users/abc', cb) — document
  // subscribe('users', { where: [...] }, cb) — collection
  if (typeof filters === 'function') {
    onError = callback;
    callback = filters;
    filters = {};
  }
  const parts = pathParts(path);
  if (parts.length % 2 === 0) {
    return onSnapshot(docRef(path), (snap) => callback(toSnap(snap)), onError);
  }
  const built = buildQuery(collRef(path), filters || {});
  return onSnapshot(built, (snap) => callback(toList(snap)), onError);
}

// ─── batch ────────────────────────────────────────────────

export function batch() {
  const b = writeBatch(db);
  return {
    set(path, id, data, options) {
      b.set(docRef(path, id), _stampTenant(path, data), options || {});
      return this;
    },
    update(path, id, data) {
      // Updates do NOT auto-stamp tenantId — see update() comment.
      b.update(docRef(path, id), data);
      return this;
    },
    remove(path, id) {
      b.delete(docRef(path, id));
      return this;
    },
    commit() {
      return b.commit();
    },
  };
}

// ─── id pre-allocation (RW-835) ───────────────────────────
// For batch writes where a child path embeds the parent's id (e.g.
// listings/{id}/tasks) or a storage path embeds the doc id, the id is
// needed before commit. Mirrors Firestore's doc(collection(...)).id.

export function newId(path) {
  return doc(collRef(path)).id;
}

// ─── transaction (RW-835) ─────────────────────────────────
// Minimal path-based wrapper over runTransaction. The callback receives
// a tx with the same path conventions as the rest of the adapter:
//   tx.get(path, id?) → { id, ...data } | null
//   tx.update(path, id, data) / tx.set(path, id, data, options?)
// Reads must precede writes (Firestore transaction rule).

export function transaction(fn) {
  return fsRunTransaction(db, async (tx) => {
    const wrapped = {
      async get(path, id) {
        const snap = await tx.get(docRef(path, id));
        return toSnap(snap);
      },
      update(path, id, data) {
        tx.update(docRef(path, id), data);
        return wrapped;
      },
      set(path, id, data, options) {
        tx.set(docRef(path, id), _stampTenant(path, data), options || {});
        return wrapped;
      },
    };
    return fn(wrapped);
  });
}

// ─── helpers re-exported so components don't import firestore directly ─

export { serverTimestamp, Timestamp };
// RW-1575: field sentinels, same rationale — the adapter stays the only
// module that knows the backing store (Rule 12).
export { deleteField, arrayUnion, arrayRemove, increment } from 'firebase/firestore';

// Escape hatch for callers that still need a raw ref (subcollection
// operations, vendor-specific listeners). New code should NOT use this.
export const _refFor = refFor;
